import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { LeadForm } from "@2u-landing-pages/components/LeadForm";
import { LeadFormIntegrated } from "@2u-landing-pages/components/LeadFormIntegrated";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { usePageState } from "@2u-landing-pages/providers";
import {
  ContentfulImageConfig,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeContent_liteWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { EduListingPayload } from "@2u-landing-pages/utils/eduListing.types";
import { Container, logEvent } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { FormProps } from "../../Form.types";
// @ts-ignore
import styles from "./Default.module.scss";

interface CustomCSSProperties extends React.CSSProperties {
  "--progress-bar-color"?: string;
  "--question-color"?: string;
  "--button-background-color"?: string;
  "--button-text-color"?: string;
}

export const Default: React.FC<FormProps> = ({
  main_content,
  section_background_color,
  section_background_image,
  image_config,
  embedded,
  taxi_package_id,
  footnote,
  topSlot,
  progress_bar_color,
  question_color,
  button_background_color,
  button_text_color,
  button_hover_state,
  enable_multi_column,
  inDrawer,
  inFlex,
  phoneNumber,
  isLoading,
  thank_you_page_url,
  form_type,
  variation,
  stepper_label_content_entries,
  stepper_active_heading_color,
  stepper_active_background_color,
  stepper_inactive_heading_text_color,
  stepper_inactive_heading_icon_color,
  dynamic_content,
  mode = "single-program",
  location,
}) => {
  const { state } = usePageState();
  const [data, setData] = useState<EduListingPayload | undefined>();
  const [activeStep, setActiveStep] = useState(1);

  let sectionBackgroundColor = section_background_color;
  if (mode === "multi-program") {
    sectionBackgroundColor = "--surface-primary-1";
  } else if (inDrawer && data?.colors?.section_background_color) {
    sectionBackgroundColor = data.colors.section_background_color as typeof section_background_color;
  }

  const style: CustomCSSProperties = useMemo(() => {
    const styleObj: CustomCSSProperties = {};

    // Use colors from event.detail if inDrawer is true and data exists, otherwise use props
    const colors = inDrawer ? data?.colors : {};
    const progressBarColor = colors?.progress_bar_color ?? progress_bar_color;
    const questionColor = colors?.question_color ?? question_color;
    const buttonBackgroundColor = colors?.button_background_color ?? button_background_color;
    const buttonTextColor = colors?.button_text_color ?? button_text_color;

    if (progressBarColor) styleObj["--progress-bar-color"] = `var(${progressBarColor})`;
    if (questionColor) styleObj["--question-color"] = `var(${questionColor})`;
    if (buttonBackgroundColor) styleObj["--button-background-color"] = `var(${buttonBackgroundColor})`;
    if (buttonTextColor) styleObj["--button-text-color"] = `var(${buttonTextColor})`;

    return styleObj;
  }, [inDrawer, data?.colors, progress_bar_color, question_color, button_background_color, button_text_color]);

  useEffect(() => {
    const handleEduListing = ((event: CustomEvent<EduListingPayload>) => {
      logEvent("open_inline_drawer_form", event.detail);
      setData(event.detail);
    }) as EventListener;

    window.addEventListener("eduListing", handleEduListing);
    return () => {
      window.removeEventListener("eduListing", handleEduListing);
    };
  }, []);

  useEffect(() => {
    if (!state.isDrawerOpen) setData(undefined);
  }, [state.isDrawerOpen]);

  const formKey = `${inDrawer && data?.form_data_package_id ? data?.form_data_package_id : taxi_package_id}${
    inDrawer ? "-drawer" : ""
  }`;

  const isStepperPlp = form_type === "integrated-plp" && variation === "stepper";

  const props = {
    isLoading,
    className: cn(styles[embedded ? "embeddedLeadForm" : "leadForm"], `btn-${button_hover_state || "dark"}`, {
      [styles.notInFlex]: !inFlex,
      [styles.inFlex]: inFlex,
      [styles.stepperLeadForm]: isStepperPlp,
      [styles.stepperLeadFormPastFirstStep]: isStepperPlp && activeStep > 1,
    }),
    packageId: inDrawer && data?.form_data_package_id ? data?.form_data_package_id : taxi_package_id || "",
    footnote: inDrawer && data?.form_data_package_id ? data?.formFootnote : footnote,
    phoneNumber,
    enableMultiColumn: inDrawer && data ? data?.enable_multi_column : enable_multi_column,
    location,
    formType: form_type,
    variation,
    stepper_label_content_entries,
    stepper_active_heading_color,
    stepper_active_background_color,
    stepper_inactive_heading_text_color,
    stepper_inactive_heading_icon_color,
  };

  const renderForm = () => {
    switch (form_type) {
      case "integrated-typ":
        return (
          <LeadFormIntegrated
            key={formKey}
            {...props}
            applyDomain={taxi_package_id}
            leadFormType="TY"
            isDrawerOpen={inDrawer ? state.isDrawerOpen : false}
            onStepChange={setActiveStep}
          />
        );
      case "integrated-plp":
        return (
          <LeadFormIntegrated
            key={formKey}
            {...props}
            thankYouURL={thank_you_page_url}
            leadFormType="LP"
            isDrawerOpen={inDrawer ? state.isDrawerOpen : false}
            onStepChange={setActiveStep}
          />
        );
      default:
        return <LeadForm key={formKey} {...props} />;
    }
  };

  const hasDynamicContent = !!dynamic_content?.length;
  const dynamicStepContent =
    hasDynamicContent && activeStep >= 2
      ? (dynamic_content![activeStep - 2] as unknown as TypeContent_liteWithoutUnresolvableLinksResponse)
      : undefined;

  const activeContent = dynamicStepContent
    ? dynamicStepContent
    : ((inDrawer && data?.main_content ? data?.main_content : main_content) as unknown as TypeContent_fullWithoutUnresolvableLinksResponse);

  const formColumns = (
    <>
      <ContentLayout
        className={cn(styles[embedded ? "embeddedContent" : "content"], {
          [styles.stepperLeadFormPastFirstStep]: isStepperPlp && activeStep > 1,
        })}
        content={activeContent}
        heading={{
          variant: embedded ? "heading-lg" : "heading-2xl",
        }}
        subhead={{
          variant: "subhead-sm",
        }}
        phoneNumber={phoneNumber}
      />
      {renderForm()}
    </>
  );
  
  if (embedded)
    return (
      <div className={styles.embeddedContainer} style={style}>
        {formColumns}
      </div>
    );

  return (
    <TemplateWrapper
      padded={!inDrawer}
      drawer={inDrawer}
      backgroundColor={!inFlex ? sectionBackgroundColor : undefined}
      backgroundImage={
        inDrawer && data?.section_background_image ? data.section_background_image : section_background_image
      }
      image_config={image_config as unknown as ContentfulImageConfig}
      style={style}
      className={styles.formTemplateWrapper}
    >
      {topSlot}
      <Container>
        <Container
          flex
          direction="column"
          lg={{ direction: "row" }}
          className={cn(styles.formContainer, {
            [styles.formContainerInDrawer]: inDrawer,
            [styles.formContainerInFlex]: inFlex,
            [styles.stepperLeadFormPastFirstStep]: isStepperPlp && activeStep > 1,
          })}
        >
          {formColumns}
        </Container>
      </Container>
    </TemplateWrapper>
  );
};
